import React, { useState, useEffect, useMemo, useId, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import measurementService from '../../../../services/measurementService';
import patientService from '../../../../services/patientService';
import { toast } from 'react-toastify';
import { normalizePeriodName } from '../../../../utils/scheduleTargetUtils';
import {
  DEFAULT_MEASUREMENT_QUERY,
  applyMeasurementFromDateChange,
  getMeasurementQueryMaxDate,
  isFromToIncomplete,
  isMeasurementQueryDirty,
  patchOnQueryModeChange,
  sanitizeMeasurementToDate,
} from '../../../../utils/queryPeriodFilters';
import { useChartContainerWidth } from '../../../../hooks/useChartContainerWidth';
import MeasurementChartHeader from '../../ChartHeader/MeasurementChartHeader';
import '../../MeasurementShell.css';
import './MeanChart.css';

/** Normalize a single BP row; swap if stored sys/dia are reversed. */
function parseBloodPressureReading(measurement) {
  if (!measurement) return null;
  let sys = Number(measurement.systolic_pressure ?? measurement.systolic);
  let dia = Number(measurement.diastolic_pressure ?? measurement.diastolic);
  if (!Number.isFinite(sys) || !Number.isFinite(dia) || sys <= 0 || dia <= 0) return null;
  if (dia > sys) {
    [sys, dia] = [dia, sys];
  }
  const date =
    measurement.measurement_date ||
    measurement.measure_new_date_time?.split('T')[0] ||
    measurement.created_at?.split('T')[0] ||
    '';
  const time =
    measurement.measurement_time ||
    measurement.measure_new_date_time?.split('T')[1]?.slice(0, 5) ||
    measurement.measure_date_time?.split(' ')[1]?.slice(0, 5) ||
    measurement.created_at?.split('T')[1]?.slice(0, 5) ||
    '';
  return { sys, dia, date, time };
}

const MeanChart = ({ practiceId, patientId, measurements: propMeasurements = null, scheduleTargets: propScheduleTargets = null, embedded = false }) => {
  const plotClipId = useId().replace(/:/g, '');
  const navigate = useNavigate();
  const [queryType, setQueryType] = useState(DEFAULT_MEASUREMENT_QUERY.queryType);
  const [dateRange, setDateRange] = useState(DEFAULT_MEASUREMENT_QUERY.dateRange);
  const [fromDate, setFromDate] = useState(DEFAULT_MEASUREMENT_QUERY.fromDate);
  const [toDate, setToDate] = useState(DEFAULT_MEASUREMENT_QUERY.toDate);
  const [patientData, setPatientData] = useState(null);
  const [measurements, setMeasurements] = useState(propMeasurements || []);
  const [scheduleTargets, setScheduleTargets] = useState(propScheduleTargets || null);
  const [loading, setLoading] = useState(!propMeasurements);
  const [queryLoading, setQueryLoading] = useState(false);

  useEffect(() => {
    if (propMeasurements) {
      setMeasurements(propMeasurements);
      setLoading(false);
    }
  }, [propMeasurements]);

  useEffect(() => {
    if (propScheduleTargets) {
      setScheduleTargets(propScheduleTargets);
    }
  }, [propScheduleTargets]);

  // Fetch patient data and schedule targets on mount
  useEffect(() => {
    if (practiceId && patientId) {
      fetchPatientData();
      if (!propScheduleTargets) fetchScheduleTargets();
    }
  }, [practiceId, patientId, propScheduleTargets]);

  // Fetch measurements on initial load / patient change only (filters apply via Query button).
  useEffect(() => {
    if (practiceId && patientId && !propMeasurements) {
      fetchMeasurements();
    }
  }, [practiceId, patientId, propMeasurements]);

  const fetchPatientData = async () => {
    try {
      const response = await patientService.getMeasurementHeaderFast(practiceId, patientId);
      if (response.data.success) {
        setPatientData(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching patient data:', error);
      toast.error('Failed to load patient data');
    }
  };

  const fetchScheduleTargets = async () => {
    try {
      const data = await patientService.getResolvedScheduleTargetsForPatient(practiceId, patientId);
      setScheduleTargets(data);
    } catch (error) {
      console.error('Error fetching schedule targets:', error);
    }
  };

  const fetchMeasurements = async () => {
    try {
      setQueryLoading(true);
      let fromDateParam, toDateParam;
      const isAllHistory = queryType === 'dateRange' && String(dateRange).toUpperCase() === 'ALL';

      if (queryType === 'dateRange') {
        if (!isAllHistory) {
          const days = parseInt(dateRange.replace(/\D/g, ''), 10) || 30;
          const endDate = new Date();
          const startDate = new Date();
          startDate.setDate(startDate.getDate() - days);
          fromDateParam = startDate.toISOString().split('T')[0];
          toDateParam = endDate.toISOString().split('T')[0];
        }
      } else {
        if (fromDate && toDate) {
          if (fromDate.includes('/')) {
            const [fromMonth, fromDay, fromYear] = fromDate.split('/');
            const [toMonth, toDay, toYear] = toDate.split('/');
            fromDateParam = `${fromYear}-${fromMonth.padStart(2, '0')}-${fromDay.padStart(2, '0')}`;
            toDateParam = `${toYear}-${toMonth.padStart(2, '0')}-${toDay.padStart(2, '0')}`;
          } else {
            fromDateParam = fromDate;
            toDateParam = toDate;
          }
        }
      }

      if (isAllHistory || (fromDateParam && toDateParam)) {
        const response = await measurementService.getBloodPressureByDateRange(
          practiceId,
          patientId,
          isAllHistory ? undefined : fromDateParam,
          isAllHistory ? undefined : toDateParam,
          'date'
        );
        if (response.data?.success) {
          setMeasurements(response.data.data || []);
        }
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching measurements:', error);
      if (error.response?.status !== 404) {
        toast.error('Failed to load measurements');
      }
      setMeasurements([]);
      setLoading(false);
    } finally {
      setQueryLoading(false);
    }
  };

  const handleQueryTypeChange = (nextType) => {
    const patch = patchOnQueryModeChange(nextType);
    setQueryType(patch.queryType);
    if (Object.prototype.hasOwnProperty.call(patch, 'fromDate')) {
      setFromDate(patch.fromDate);
      setToDate(patch.toDate);
    }
  };

  const handleQuery = () => {
    if (isFromToIncomplete(queryType, fromDate, toDate)) {
      toast.warning('Please select both start and end dates');
      return;
    }
    fetchMeasurements();
  };

  const handleReset = () => {
    setQueryType(DEFAULT_MEASUREMENT_QUERY.queryType);
    setDateRange(DEFAULT_MEASUREMENT_QUERY.dateRange);
    setFromDate(DEFAULT_MEASUREMENT_QUERY.fromDate);
    setToDate(DEFAULT_MEASUREMENT_QUERY.toDate);
  };

  const isQueryDisabled = queryLoading || isFromToIncomplete(queryType, fromDate, toDate);
  const isResetDisabled = queryLoading || !isMeasurementQueryDirty({
    queryType, dateRange, fromDate, toDate, sortBy: DEFAULT_MEASUREMENT_QUERY.sortBy,
  });

  const patientName = patientData?.patient
    ? `${patientData.patient.first_name || ''} ${patientData.patient.last_name || ''}`.trim()
    : 'Loading...';
  const patientDOB = patientData?.patient?.date_of_birth
    ? new Date(patientData.patient.date_of_birth).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
    : 'Loading...';

  // Get period names from schedule targets
  const periodNames = useMemo(() => {
    if (!scheduleTargets?.bloodPressureTimePeriod) {
      return ['Wake-Up', 'Morning', 'Noon', 'Afternoon', 'Evening'];
    }
    return scheduleTargets.bloodPressureTimePeriod.map(p => p.name);
  }, [scheduleTargets]);

  // Calculate mean values by period
  const periodMeans = useMemo(() => {
    if (!measurements || measurements.length === 0) {
      return periodNames.map(name => ({ name, sys: null, dia: null, readings: [] }));
    }

    return periodNames.map(periodName => {
      const periodMeasurements = measurements.filter(m => {
        const raw = m.period_name || m.period || m.measure_note || m.note || 'Unknown';
        return normalizePeriodName(raw) === normalizePeriodName(periodName);
      });

      if (periodMeasurements.length === 0) {
        return { name: periodName, sys: null, dia: null, readings: [] };
      }

      const readings = periodMeasurements.map(parseBloodPressureReading).filter(Boolean);

      const sysValues = readings.map((p) => Number(p.sys)).filter(v => Number.isFinite(v) && v > 0);
      const diaValues = readings.map((p) => Number(p.dia)).filter(v => Number.isFinite(v) && v > 0);

      const sysMean = sysValues.length > 0
        ? sysValues.reduce((a, b) => a + Number(b), 0) / sysValues.length
        : null;
      const diaMean = diaValues.length > 0
        ? diaValues.reduce((a, b) => a + Number(b), 0) / diaValues.length
        : null;

      return { name: periodName, sys: sysMean, dia: diaMean, readings };
    });
  }, [measurements, periodNames]);

  const chartMaxY = useMemo(() => {
    const values = [];
    periodMeans.forEach((p) => {
      if (p.sys != null && Number.isFinite(Number(p.sys))) values.push(Number(p.sys));
      if (p.dia != null && Number.isFinite(Number(p.dia))) values.push(Number(p.dia));
    });
    if (values.length === 0) return 200;
    const peak = Math.max(...values);
    if (!Number.isFinite(peak) || peak <= 0) return 200;
    return Math.max(200, Math.ceil(peak / 20) * 20);
  }, [periodMeans]);

  const yAxisTicks = useMemo(() => {
    const maxY = Number.isFinite(chartMaxY) && chartMaxY > 0 ? chartMaxY : 200;
    const rawStep = maxY <= 200 ? 20 : maxY <= 500 ? 50 : Math.ceil(maxY / 8 / 50) * 50;
    const step = Math.max(10, rawStep);
    const ticks = [];
    for (let v = 0; v <= maxY; v += step) {
      ticks.push(v);
    }
    if (ticks[ticks.length - 1] !== maxY) {
      ticks.push(maxY);
    }
    return ticks;
  }, [chartMaxY]);

  const [chartAreaRef, chartWidth] = useChartContainerWidth(640, 1100);
  const chartHeight = embedded ? 300 : 400;
  const padding = { top: 20, right: 30, bottom: 60, left: 105 };
  const plotWidth = chartWidth - padding.left - padding.right;
  const plotHeight = chartHeight - padding.top - padding.bottom;
  const minY = 0;
  const maxY = chartMaxY;
  const plotBottom = padding.top + plotHeight;

  // Scale functions — center each period in its own band so bars stay inside the plot
  const periodCount = Math.max(1, periodNames.length);
  const bandWidth = plotWidth / periodCount;

  const scaleX = (index) => {
    return padding.left + bandWidth * index + bandWidth / 2;
  };

  const scaleY = (value) => {
    if (value == null || Number.isNaN(value)) return null;
    const clamped = Math.min(maxY, Math.max(minY, value));
    return padding.top + plotHeight - ((clamped - minY) / (maxY - minY)) * plotHeight;
  };

  const barGeometry = (value) => {
    const yTop = scaleY(value);
    if (yTop == null) return null;
    const top = Math.max(padding.top, yTop);
    return { y: top, height: Math.max(0, plotBottom - top) };
  };

  const barWidth = Math.min(36, Math.max(10, bandWidth / 2 - 6));

  const [tooltip, setTooltip] = useState(null);
  const chartSurfaceRef = useRef(null);
  const tooltipHideTimerRef = useRef(null);

  const cancelTooltipHide = () => {
    if (tooltipHideTimerRef.current) {
      clearTimeout(tooltipHideTimerRef.current);
      tooltipHideTimerRef.current = null;
    }
  };

  const scheduleTooltipHide = () => {
    cancelTooltipHide();
    tooltipHideTimerRef.current = setTimeout(() => {
      setTooltip(null);
      tooltipHideTimerRef.current = null;
    }, 150);
  };

  useEffect(() => () => cancelTooltipHide(), []);

  const formatReadingTime = (date, time) => {
    if (!time) return date || '';
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours, 10);
    if (Number.isNaN(hour)) return time;
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    const datePart = date ? `${date} ` : '';
    return `${datePart}${displayHour}:${minutes} ${ampm}`.trim();
  };

  const showPeriodTooltip = (period, index) => {
    if (period.sys == null && period.dia == null) return;
    cancelTooltipHide();
    const rect = chartSurfaceRef.current?.getBoundingClientRect();
    if (!rect) return;
    const scaledBandCenter = (scaleX(index) / chartWidth) * rect.width;
    const tooltipHalfWidth = Math.min(140, Math.max(90, rect.width / 2 - 8));
    const anchoredX = Math.min(
      Math.max(scaledBandCenter, tooltipHalfWidth),
      Math.max(tooltipHalfWidth, rect.width - tooltipHalfWidth)
    );
    setTooltip({
      periodName: period.name,
      sys: period.sys,
      dia: period.dia,
      readings: period.readings || [],
      x: anchoredX,
    });
  };

  return (
    <div className={`mean-chart-page${embedded ? ' mean-chart-page--embedded' : ''}`}>
      {!embedded && (
        <MeasurementChartHeader
          activeVital="blood-pressure"
          queryType={queryType}
          setQueryType={setQueryType}
          dateRange={dateRange}
          setDateRange={setDateRange}
          fromDate={fromDate}
          setFromDate={setFromDate}
          toDate={toDate}
          setToDate={setToDate}
          onQuery={handleQuery}
          onReset={handleReset}
          queryDisabled={isQueryDisabled}
          resetDisabled={isResetDisabled}
          onPrint={() => {}}
          onExportPdf={() => {}}
          onExportCsv={() => {}}
          onEmail={() => {}}
        />
      )}

      {(() => {
        const chartBody = (
      <div className="mc-fieldset mc-section-card" role="group" aria-labelledby="mc-section-title">
        {!embedded && (
          <div id="mc-section-title" className="mc-section-label">
            Measurement Period Comparison Chart
          </div>
        )}
        <div className="mc-chart-container">
          {!embedded ? (
            <div className="mc-chart-head">
              <div className="mc-chart-title-center mc-chart-title-inline">
                Measurement Period Comparison Chart of Blood Pressure
              </div>
              <div className="mc-chart-legend">
                <div className="mc-legend-item">
                  <div className="mc-legend-dot mc-legend-systolic"></div>
                  <span>Systolic Pressure</span>
                </div>
                <div className="mc-legend-item">
                  <div className="mc-legend-dot mc-legend-diastolic"></div>
                  <span>Diastolic Pressure</span>
                </div>
              </div>
            </div>
          ) : null}
          <div className="mc-chart-area" ref={chartAreaRef}>
            {loading || queryLoading ? (
              <div className="mc-chart-status">Loading chart data...</div>
            ) : periodMeans.every(p => p.sys == null && p.dia == null) ? (
              <div className="mc-chart-status">No data available for the selected period</div>
            ) : (
              <div className="mc-chart-surface" ref={chartSurfaceRef}>
                <svg
                  viewBox={`0 0 ${chartWidth} ${chartHeight}`}
                  width={chartWidth}
                  height={chartHeight}
                  preserveAspectRatio="xMidYMid meet"
                  className="mc-chart-svg"
                  overflow="hidden"
                  style={{ pointerEvents: 'bounding-box' }}
                >
                  <defs>
                    <clipPath id={plotClipId}>
                      <rect x={padding.left} y={padding.top} width={plotWidth} height={plotHeight} />
                    </clipPath>
                  </defs>
                  {/* Y-axis Label — shifted left */}
                  <text
                    x={22}
                    y={padding.top + plotHeight / 2}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fill="#333"
                    fontSize="14"
                    fontWeight="600"
                    transform={`rotate(-90, 22, ${padding.top + plotHeight / 2})`}
                  >
                    Blood Pressure mmHg
                  </text>

                  {/* Grid border */}
                  <rect x={padding.left} y={padding.top} width={plotWidth} height={plotHeight} fill="none" stroke="#e0e0e0" strokeWidth="1" />

                  {/* Horizontal Grid lines and Y-axis text */}
                  {[...yAxisTicks].reverse().map((value) => {
                    const y = scaleY(value);
                    if (y == null) return null;
                    return (
                      <g key={`y-${value}`}>
                        <line
                          x1={padding.left}
                          y1={y}
                          x2={chartWidth - padding.right}
                          y2={y}
                          stroke="#efefef"
                          strokeWidth="1"
                        />
                        <text
                          x={padding.left - 12}
                          y={y}
                          textAnchor="end"
                          dominantBaseline="middle"
                          fill="#666"
                          fontSize="12"
                        >
                          {value}
                        </text>
                      </g>
                    );
                  })}

                  {/* Vertical grid lines for each period */}
                  {periodNames.map((periodName, index) => {
                    const x = scaleX(index);
                    return (
                      <g key={`vl-${periodName}`}>
                        <line
                          x1={x}
                          y1={padding.top}
                          x2={x}
                          y2={chartHeight - padding.bottom}
                          stroke="#e0e0e0"
                          strokeWidth="1"
                        />
                        {/* X-axis Text — period names */}
                        <text
                          x={x}
                          y={chartHeight - padding.bottom + 20}
                          textAnchor="middle"
                          dominantBaseline="middle"
                          fill="#333"
                          fontSize="13"
                          fontWeight="500"
                        >
                          {periodName}
                        </text>
                      </g>
                    );
                  })}

                  <g clipPath={`url(#${plotClipId})`}>
                  {/* Systolic bars (red) */}
                  {periodMeans.map((period, index) => {
                    if (period.sys == null) return null;
                    const x = scaleX(index);
                    const geom = barGeometry(period.sys);
                    if (!geom) return null;
                    const { y, height: barH } = geom;

                    return (
                      <g
                        key={`sys-${period.name}`}
                        style={{ cursor: 'pointer' }}
                        onMouseEnter={() => showPeriodTooltip(period, index)}
                        onMouseLeave={scheduleTooltipHide}
                        onFocus={() => showPeriodTooltip(period, index)}
                        onBlur={scheduleTooltipHide}
                        tabIndex={0}
                        role="img"
                        aria-label={`${period.name}, systolic ${Math.round(period.sys)} mmHg`}
                      >
                        <rect
                          x={x - barWidth - 2}
                          y={y}
                          width={barWidth}
                          height={barH}
                          fill="#e53e3e"
                          rx="2"
                        />
                        <text
                          x={x - barWidth / 2 - 2}
                          y={barH > 25 ? y + 18 : y - 8}
                          fill={barH > 25 ? '#ffffff' : '#e53e3e'}
                          textAnchor="middle"
                          fontSize="12"
                          fontWeight="bold"
                          pointerEvents="none"
                        >
                          {Math.round(period.sys)}
                        </text>
                      </g>
                    );
                  })}

                  {/* Diastolic bars (blue) */}
                  {periodMeans.map((period, index) => {
                    if (period.dia == null) return null;
                    const x = scaleX(index);
                    const geom = barGeometry(period.dia);
                    if (!geom) return null;
                    const { y, height: barH } = geom;

                    return (
                      <g
                        key={`dia-${period.name}`}
                        style={{ cursor: 'pointer' }}
                        onMouseEnter={() => showPeriodTooltip(period, index)}
                        onMouseLeave={scheduleTooltipHide}
                        onFocus={() => showPeriodTooltip(period, index)}
                        onBlur={scheduleTooltipHide}
                        tabIndex={0}
                        role="img"
                        aria-label={`${period.name}, diastolic ${Math.round(period.dia)} mmHg`}
                      >
                        <rect
                          x={x + 2}
                          y={y}
                          width={barWidth}
                          height={barH}
                          fill="#1a3550"
                          rx="2"
                        />
                        <text
                          x={x + barWidth / 2 + 2}
                          y={barH > 25 ? y + 18 : y - 8}
                          fill={barH > 25 ? '#ffffff' : '#1a3550'}
                          textAnchor="middle"
                          fontSize="12"
                          fontWeight="bold"
                          pointerEvents="none"
                        >
                          {Math.round(period.dia)}
                        </text>
                      </g>
                    );
                  })}
                  </g>

                </svg>

                <div style={{ textAlign: 'center', marginTop: '10px', color: '#666', fontSize: '13px', fontWeight: '500' }}>
                  Time of Day (5 AM - 5 AM)
                </div>

                {tooltip && (
                  <div
                    className="mc-tooltip"
                    style={{
                      left: `${tooltip.x}px`,
                      top: '8px',
                      transform: 'translateX(-50%)',
                    }}
                    onMouseEnter={cancelTooltipHide}
                    onMouseLeave={scheduleTooltipHide}
                    onWheel={(event) => event.stopPropagation()}
                  >
                    <div className="mc-tooltip-content">
                      <div className="mc-tooltip-title">{tooltip.periodName}</div>
                      {tooltip.readings.length > 1 && (
                        <div className="mc-tooltip-subtitle">Period average</div>
                      )}
                      {tooltip.sys != null && (
                        <div className="mc-tooltip-item">
                          <span>Systolic</span>
                          <span className="mc-tooltip-sys">{Math.round(tooltip.sys)} mmHg</span>
                        </div>
                      )}
                      {tooltip.dia != null && (
                        <div className="mc-tooltip-item">
                          <span>Diastolic</span>
                          <span className="mc-tooltip-dia">{Math.round(tooltip.dia)} mmHg</span>
                        </div>
                      )}
                      {tooltip.readings.length > 0 && (
                        <div className="mc-tooltip-readings">
                          <div className="mc-tooltip-readings-title">
                            Readings ({tooltip.readings.length})
                          </div>
                          {tooltip.readings.map((r, idx) => (
                            <div key={`${r.date}-${r.time}-${idx}`} className="mc-tooltip-reading-row">
                              <span>{formatReadingTime(r.date, r.time)}</span>
                              <span>
                                {Math.round(r.sys)}/{Math.round(r.dia)} mmHg
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
        );

        if (embedded) {
          return chartBody;
        }

        return (
          <div className="bp-section">
            <div className="bp-section-title-row">
              <h2 className="bp-section-title">Mean Chart</h2>
            </div>
            <div className="bp-card">
              <div className="bp-card-body">{chartBody}</div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default MeanChart;
